import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format, parseISO } from "date-fns";
import {
  Calendar,
  MapPin,
  Users,
  Ticket,
  CheckCircle,
  Clock,
  Mail,
  Phone,
  Search,
  Star,
  Loader2,
  AlertCircle,
  User,
  DoorOpen,
  UserX,
  ChevronRight,
  ChevronDown,
  List,
  X,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTER = {
  ALL: "all",
  PENDING: "pending",
  CONFIRMED: "confirmed",
  CHECKED_IN: "checked_in",
  NO_SHOW: "no_show",
};

const TICKET_TYPE_LABELS = {
  early_bird: "Early Bird",
  regular: "Regular",
  vip: "VIP",
};

const TICKET_TYPE_COLORS = {
  early_bird: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  regular: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  vip: "bg-purple-500/20 text-purple-400 border-purple-500/30",
};

const STATUS_COLORS = {
  draft: "bg-neutral-500/20 text-neutral-400 border-neutral-500/30",
  published: "bg-green-500/20 text-green-400 border-green-500/30",
  cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
  completed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
};

export const AdminEventRoster = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState(searchParams.get("event") || null);
  const [roster, setRoster] = useState(null);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState(FILTER.ALL);
  const [sortBy, setSortBy] = useState("recent");
  const [searchQuery, setSearchQuery] = useState("");
  const [eventSearch, setEventSearch] = useState("");
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchAllEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      fetchRoster(selectedEventId);
      setActiveFilter(FILTER.ALL);
      setSearchQuery("");
    } else {
      setRoster(null);
    }
  }, [selectedEventId]);

  // Close overlay on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOverlayOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const fetchAllEvents = async () => {
    setEventsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/events/all`);
      const data = res.data || [];
      setEvents(data);
      const paramEvent = searchParams.get("event");
      if (paramEvent && data.some((e) => e.id === paramEvent)) {
        setSelectedEventId(paramEvent);
      } else if (data.length > 0 && !paramEvent) {
        setSelectedEventId(data[0].id);
      }
    } catch {
      toast.error("Failed to load events");
    } finally {
      setEventsLoading(false);
    }
  };

  const fetchRoster = async (eventId) => {
    setRosterLoading(true);
    try {
      const res = await axios.get(`${API}/admin/event-roster?event_id=${eventId}`);
      setRoster(res.data);
    } catch {
      toast.error("Failed to load event roster");
      setRoster(null);
    } finally {
      setRosterLoading(false);
    }
  };

  const handleCheckIn = (bookingId, guestName) => {
    toast(`Check in ${guestName}?`, {
      description: "Mark this guest as arrived at the event.",
      action: {
        label: "Check In",
        onClick: async () => {
          try {
            await axios.post(`${API}/admin/bookings/${bookingId}/check-in`);
            toast.success(`${guestName} checked in`);
            fetchRoster(selectedEventId);
          } catch (err) {
            toast.error(err.response?.data?.detail || "Check-in failed");
          }
        },
      },
      cancel: { label: "Cancel", onClick: () => {} },
    });
  };

  const handleSelectEvent = (eventId) => {
    setSelectedEventId(eventId);
    setSearchParams({ event: eventId });
    setOverlayOpen(false);
  };

  useEffect(() => { setPage(1); }, [activeFilter, searchQuery, sortBy]);

  // Client-side filtered bookings — no API calls on search/filter change
  const filteredBookings = useMemo(() => {
    const list = roster?.bookings || [];
    let result = list;
    switch (activeFilter) {
      case FILTER.PENDING:
        result = list.filter((b) => b.payment_status !== "paid");
        break;
      case FILTER.CONFIRMED:
        result = list.filter((b) => b.payment_status === "paid" && !b.checked_in_at && !b.is_no_show);
        break;
      case FILTER.CHECKED_IN:
        result = list.filter((b) => b.checked_in_at);
        break;
      case FILTER.NO_SHOW:
        result = list.filter((b) => b.is_no_show);
        break;
      default:
        result = list;
    }
    if (!searchQuery.trim()) return result;
    const q = searchQuery.toLowerCase();
    const sorted = [...result].sort((a, b) => {
      if (sortBy === "amount") return (b.total_amount || 0) - (a.total_amount || 0);
      if (sortBy === "tickets") return (b.ticket_quantity || 0) - (a.ticket_quantity || 0);
      if (sortBy === "name")
        return (a.guest_name || "").localeCompare(b.guest_name || "");
      const da = new Date(a.created_at || 0).getTime();
      const db_ = new Date(b.created_at || 0).getTime();
      return sortBy === "oldest" ? da - db_ : db_ - da;
    });
    result = sorted;
    return result.filter(
      (b) =>
        b.guest_name?.toLowerCase().includes(q) ||
        b.guest_email?.toLowerCase().includes(q) ||
        b.guest_phone?.toLowerCase().includes(q) ||
        b.booking_number?.toLowerCase().includes(q) ||
        b.ticket_type_name?.toLowerCase().includes(q)
    );
  }, [roster?.bookings, activeFilter, searchQuery, sortBy]);

  const PAGE_SIZE = 10;
  const totalPages = Math.ceil(filteredBookings.length / PAGE_SIZE);
  const paginatedBookings = filteredBookings.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Client-side event list search (overlay panel)
  const filteredEvents = useMemo(() => {
    if (!eventSearch.trim()) return events;
    const q = eventSearch.toLowerCase();
    return events.filter((e) => e.name?.toLowerCase().includes(q));
  }, [events, eventSearch]);

  const stats = roster?.stats;
  const event = roster?.event;

  // Derived money/ticket metrics — all computed from paid bookings only.
  const paidBookings = useMemo(
    () => (roster?.bookings || []).filter((b) => b.payment_status === "paid"),
    [roster?.bookings]
  );
  const pendingValue = useMemo(
    () =>
      (roster?.bookings || [])
        .filter((b) => b.payment_status !== "paid" && b.status !== "cancelled")
        .reduce((sum, b) => sum + (b.total_amount || 0), 0),
    [roster?.bookings]
  );
  const avgOrder = paidBookings.length
    ? Math.round((stats?.total_revenue ?? 0) / paidBookings.length)
    : 0;
  const ticketsPerBuyer = paidBookings.length
    ? ((stats?.tickets_paid ?? 0) / paidBookings.length).toFixed(1)
    : "0";
  const selectedEventMeta = events.find((e) => e.id === selectedEventId);

  const statCardCls = (filter) =>
    `card-empire transition-all text-left w-full ${
      activeFilter === filter
        ? "ring-2 ring-empire-gold/60 border-empire-gold/40"
        : "hover:border-neutral-600"
    }`;

  const formatEAT = (isoStr) => {
    if (!isoStr) return null;
    try { return format(parseISO(isoStr), "MMM d, h:mm a"); }
    catch { return isoStr; }
  };

  const formatDateRange = (start, end) => {
    if (!start) return "—";
    try {
      const s = parseISO(start);
      const e = end ? parseISO(end) : null;
      if (!e) return format(s, "MMM d, yyyy · h:mm a");
      return `${format(s, "MMM d, yyyy")} · ${format(s, "h:mm a")} – ${format(e, "h:mm a")}`;
    } catch { return start; }
  };

  // ─── Overlay event list panel (desktop) ────────────────────────────────────
  const OverlayPanel = () => (
    <AnimatePresence>
      {overlayOpen && (
        <>
          {/* Backdrop — covers only the content area (starts after lg:ml-64 sidebar) */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setOverlayOpen(false)}
            className="fixed top-0 left-0 lg:left-64 right-0 bottom-0 z-30 bg-black/60 backdrop-blur-sm"
          />

          {/* Slide-in panel */}
          <motion.div
            key="panel"
            initial={{ x: -340 }}
            animate={{ x: 0 }}
            exit={{ x: -340 }}
            transition={{ type: "spring", damping: 28, stiffness: 220 }}
            className="fixed top-0 left-0 lg:left-64 bottom-0 z-40 w-80 flex flex-col bg-neutral-950 border-r border-neutral-800 shadow-2xl"
          >
            {/* Current event summary */}
            {selectedEventMeta && (
              <div className="flex-shrink-0 p-4 border-b border-neutral-800 bg-neutral-900/60">
                <p className="text-[10px] font-semibold text-neutral-500 uppercase tracking-widest mb-2">
                  Currently Viewing
                </p>
                <p className="font-semibold text-white text-sm leading-snug">
                  {selectedEventMeta.name}
                </p>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  {selectedEventMeta.start_datetime && (
                    <span className="text-xs text-neutral-400">
                      {format(parseISO(selectedEventMeta.start_datetime), "MMM d, yyyy")}
                    </span>
                  )}
                  <Badge className={`text-[10px] px-1.5 py-0 ${STATUS_COLORS[selectedEventMeta.status] || STATUS_COLORS.draft}`}>
                    {selectedEventMeta.status}
                  </Badge>
                  <span className="text-xs text-neutral-500">
                    {selectedEventMeta.booking_count ?? 0} bookings
                  </span>
                </div>
              </div>
            )}

            {/* Search */}
            <div className="flex-shrink-0 p-3 border-b border-neutral-800">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-xs font-semibold text-neutral-300 flex-1">Switch Event</p>
                <button
                  onClick={() => setOverlayOpen(false)}
                  className="text-neutral-500 hover:text-white transition-colors p-1 rounded-lg hover:bg-neutral-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-500" />
                <Input
                  placeholder="Search events..."
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                  className="input-empire pl-8 h-9 text-sm"
                  autoFocus
                />
              </div>
            </div>

            {/* Event list */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {filteredEvents.length === 0 ? (
                <p className="text-neutral-500 text-sm text-center py-6">No events match</p>
              ) : (
                filteredEvents.map((ev) => (
                  <button
                    key={ev.id}
                    onClick={() => handleSelectEvent(ev.id)}
                    className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
                      selectedEventId === ev.id
                        ? "bg-empire-gold/10 border-empire-gold/40"
                        : "bg-neutral-900/50 border-neutral-800/60 hover:border-neutral-700 hover:bg-neutral-900"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-medium text-white text-sm leading-snug line-clamp-1">
                        {ev.name}
                      </span>
                      {selectedEventId === ev.id && (
                        <CheckCircle className="w-3.5 h-3.5 text-empire-gold flex-shrink-0 mt-0.5" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {ev.start_datetime && (
                        <span className="text-xs text-neutral-400">
                          {format(parseISO(ev.start_datetime), "MMM d, yyyy")}
                        </span>
                      )}
                      <Badge className={`text-[10px] px-1.5 py-0 ${STATUS_COLORS[ev.status] || STATUS_COLORS.draft}`}>
                        {ev.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      {ev.booking_count ?? 0} {(ev.booking_count ?? 0) === 1 ? "booking" : "bookings"}
                      {ev.max_capacity ? ` · cap ${ev.max_capacity}` : ""}
                    </p>
                  </button>
                ))
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div data-testid="admin-event-roster">
      {/* Desktop overlay */}
      <OverlayPanel />

      {/* Page header */}
      <div className="mb-5">
        <h1 className="text-2xl md:text-3xl font-bold text-white">Event Roster</h1>
        <p className="text-neutral-400 text-sm mt-1">
          Manage event bookings, check-ins and attendance
        </p>
      </div>

      {eventsLoading ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <Card className="card-empire">
          <CardContent className="py-16 text-center">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-neutral-600" />
            <p className="text-neutral-400">No events found. Create an event first.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* ── Event selector bar ───────────────────────────────────── */}
          <div className="mb-6">
            {/* Desktop: breadcrumb trigger */}
            <div className="hidden lg:block">
              <button
                onClick={() => setOverlayOpen(true)}
                className="group flex items-center gap-1.5 px-3 py-2 rounded-xl bg-neutral-900 border border-neutral-800 hover:border-empire-gold/40 hover:bg-neutral-900/80 transition-all"
              >
                <List className="w-4 h-4 text-neutral-500 group-hover:text-empire-gold transition-colors" />
                <span className="text-sm text-neutral-400">Events</span>
                <ChevronRight className="w-3.5 h-3.5 text-neutral-600" />
                <span className="text-sm font-medium text-white">
                  {selectedEventMeta?.name || "Select an event"}
                </span>
                {selectedEventMeta && (
                  <Badge className={`ml-1 text-[10px] px-1.5 py-0 ${STATUS_COLORS[selectedEventMeta.status] || STATUS_COLORS.draft}`}>
                    {selectedEventMeta.status}
                  </Badge>
                )}
                <ChevronDown className="w-3.5 h-3.5 text-neutral-500 ml-1 group-hover:text-empire-gold transition-colors" />
              </button>
            </div>

            {/* Mobile: styled dropdown */}
            <div className="lg:hidden">
              <Select
                value={selectedEventId || ""}
                onValueChange={handleSelectEvent}
              >
                <SelectTrigger className="bg-neutral-900 border-neutral-800 text-white h-11 focus:border-empire-gold/50 focus:ring-empire-gold/20">
                  <div className="flex items-center gap-2 min-w-0">
                    <Calendar className="w-4 h-4 text-empire-gold flex-shrink-0" />
                    <SelectValue placeholder="Select an event..." />
                  </div>
                </SelectTrigger>
                <SelectContent className="bg-neutral-950 border-neutral-800 max-h-72">
                  {events.map((ev) => (
                    <SelectItem
                      key={ev.id}
                      value={ev.id}
                      className="text-white focus:bg-empire-gold/10 focus:text-empire-gold"
                    >
                      <span className="flex flex-col">
                        <span className="font-medium">{ev.name}</span>
                        <span className="text-xs text-neutral-400">
                          {ev.start_datetime
                            ? format(parseISO(ev.start_datetime), "MMM d, yyyy")
                            : "No date"}{" "}
                          · {ev.status} · {ev.booking_count ?? 0} bookings
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ── Roster content (always full width) ───────────────────── */}
          {rosterLoading ? (
            <Card className="card-empire">
              <CardContent className="py-20 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-empire-gold animate-spin" />
              </CardContent>
            </Card>
          ) : roster ? (
            <motion.div
              key={selectedEventId}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              {/* ── Row 1: Event info + cover ──────────────────────────── */}
              <Card className="card-empire overflow-hidden">
                <div className="flex flex-col sm:flex-row">
                  {event?.cover_image && (
                    <div className="w-full sm:w-52 h-40 sm:h-auto flex-shrink-0">
                      <img
                        src={event.cover_image}
                        alt={event.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardContent className="flex-1 p-5">
                    {/* Name + badges */}
                    <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                      <div>
                        <h2 className="text-xl font-bold text-white">{event?.name}</h2>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <Badge className={STATUS_COLORS[event?.status] || STATUS_COLORS.draft}>
                            {event?.status}
                          </Badge>
                          {event?.event_type && (
                            <Badge variant="outline" className="border-neutral-700 text-neutral-400 text-xs">
                              {event.event_type}
                            </Badge>
                          )}
                          {event?.is_featured && (
                            <Badge className="bg-empire-gold/20 text-empire-gold border-empire-gold/30 text-xs">
                              <Star className="w-3 h-3 mr-1 fill-current" />
                              Featured
                            </Badge>
                          )}
                          {stats?.event_ended && (
                            <Badge className="bg-neutral-700/50 text-neutral-300 border-neutral-600 text-xs">
                              Event Ended
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Details grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-2 text-sm text-neutral-400 mb-4">
                      <span className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-empire-gold flex-shrink-0" />
                        {formatDateRange(event?.start_datetime, event?.end_datetime)}
                      </span>
                      {event?.venue_name && (
                        <span className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-empire-gold flex-shrink-0" />
                          {event.venue_name}
                        </span>
                      )}
                      <span className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-empire-gold flex-shrink-0" />
                        {stats?.tickets_paid ?? 0} tickets paid
                        {event?.max_capacity ? ` / ${event.max_capacity} capacity` : ""}
                        {stats?.tickets_pending > 0 && (
                          <span className="text-yellow-500/80"> · {stats.tickets_pending} unpaid</span>
                        )}
                      </span>
                      {event?.venue_address && (
                        <span className="flex items-center gap-2 text-xs text-neutral-500 col-span-full sm:col-span-1">
                          <span className="w-4 flex-shrink-0" />
                          {event.venue_address}
                        </span>
                      )}
                    </div>

                    {/* Capacity bar */}
                    {event?.max_capacity > 0 && (
                      <div className="mb-4">
                        <div className="flex justify-between text-xs text-neutral-500 mb-1.5">
                          <span>
                            Capacity ·{" "}
                            <span className="text-empire-gold font-medium tabular-nums">
                              {stats?.tickets_paid ?? 0}
                            </span>
                            {" / "}
                            <span className="tabular-nums">{event.max_capacity}</span> tickets
                          </span>
                          <span className="tabular-nums">{stats?.capacity_used_pct ?? 0}% sold</span>
                        </div>
                        <div className="h-2 bg-neutral-800 rounded-full overflow-hidden flex">
                          <div
                            className="h-full bg-empire-gold transition-all duration-700"
                            style={{ width: `${Math.min(stats?.capacity_used_pct ?? 0, 100)}%` }}
                            title={`${stats?.tickets_paid ?? 0} paid`}
                          />
                          <div
                            className="h-full bg-yellow-500/30 transition-all duration-700"
                            style={{
                              width: `${Math.min(
                                ((stats?.tickets_pending ?? 0) / event.max_capacity) * 100,
                                Math.max(100 - (stats?.capacity_used_pct ?? 0), 0)
                              )}%`,
                            }}
                            title={`${stats?.tickets_pending ?? 0} unpaid (not counted)`}
                          />
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[11px] text-neutral-500">
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-sm bg-empire-gold" />
                            {stats?.tickets_paid ?? 0} paid
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-sm bg-yellow-500/30" />
                            {stats?.tickets_pending ?? 0} unpaid
                          </span>
                          {stats?.tickets_failed > 0 && (
                            <span>{stats.tickets_failed} failed</span>
                          )}
                          <span className="text-neutral-600">
                            {Math.max(event.max_capacity - (stats?.tickets_paid ?? 0), 0)} still available
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Highlights */}
                    {event?.highlights?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {event.highlights.map((h, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 text-xs bg-empire-gold/10 border border-empire-gold/20 text-empire-gold px-2.5 py-0.5 rounded-full"
                          >
                            <Star className="w-2.5 h-2.5 fill-current flex-shrink-0" />
                            {h}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Ticket types inline */}
                    {event?.ticket_types?.length > 0 && (
                      <div className="pt-3 border-t border-neutral-800">
                        <p className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                          <Ticket className="w-3 h-3 text-empire-gold" />
                          Ticket Types
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {event.ticket_types.map((tt) => {
                            const rev = stats?.revenue_by_ticket_type?.find(
                              (r) => r.ticket_type_id === tt.id
                            );
                            return (
                              <div
                                key={tt.id}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-neutral-900/80 border border-neutral-800 text-sm"
                              >
                                <Badge className={`text-[10px] px-1.5 py-0 ${TICKET_TYPE_COLORS[tt.ticket_type] || TICKET_TYPE_COLORS.regular}`}>
                                  {TICKET_TYPE_LABELS[tt.ticket_type] || tt.ticket_type}
                                </Badge>
                                <span className="text-white font-medium">{tt.name}</span>
                                <span className="text-neutral-500">·</span>
                                <span className="text-empire-gold font-semibold">
                                  KES {(tt.price || 0).toLocaleString()}
                                </span>
                                {rev && (
                                  <>
                                    <span className="text-neutral-600">·</span>
                                    <span className="text-neutral-400 text-xs">{rev.count} sold</span>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </div>
              </Card>

              {/* ── Money & tickets — the two numbers that matter most ──── */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="card-empire md:col-span-2">
                  <CardContent className="p-4">
                    <p className="text-xs text-neutral-500 uppercase tracking-wider">Revenue Collected</p>
                    <p className="text-3xl font-bold text-empire-gold tabular-nums mt-1">
                      KES {(stats?.total_revenue ?? 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-neutral-500 mt-1">
                      From {stats?.confirmed + stats?.checked_in + stats?.no_show || 0} paid bookings
                      {stats?.tickets_pending > 0 && (
                        <span className="text-yellow-500/80">
                          {" "}· KES {(pendingValue).toLocaleString()} outstanding
                        </span>
                      )}
                    </p>
                  </CardContent>
                </Card>
                <Card className="card-empire">
                  <CardContent className="p-4">
                    <p className="text-xs text-neutral-500 uppercase tracking-wider">Tickets Sold</p>
                    <p className="text-3xl font-bold text-white tabular-nums mt-1">
                      {stats?.tickets_paid ?? 0}
                    </p>
                    <p className="text-xs text-neutral-500 mt-1">
                      of {stats?.capacity ?? event?.max_capacity ?? 0} capacity
                    </p>
                  </CardContent>
                </Card>
                <Card className="card-empire">
                  <CardContent className="p-4">
                    <p className="text-xs text-neutral-500 uppercase tracking-wider">Avg Order</p>
                    <p className="text-3xl font-bold text-white tabular-nums mt-1">
                      KES {avgOrder.toLocaleString()}
                    </p>
                    <p className="text-xs text-neutral-500 mt-1">
                      {ticketsPerBuyer} tickets per buyer
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* ── Row 2: Stats row ────────────────────────────────────── */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card className={statCardCls(FILTER.ALL)}>
                  <CardContent className="p-0">
                    <button type="button" onClick={() => setActiveFilter(FILTER.ALL)}
                      className="w-full py-3 px-2 text-center rounded-lg"
                      aria-pressed={activeFilter === FILTER.ALL}>
                      <p className="text-2xl font-bold text-white tabular-nums">{stats?.total ?? 0}</p>
                      <p className="text-xs text-neutral-400">Total</p>
                      <p className="text-[10px] text-neutral-600 mt-0.5">Show all</p>
                    </button>
                  </CardContent>
                </Card>

                <Card className={statCardCls(FILTER.PENDING)}>
                  <CardContent className="p-0">
                    <button type="button" onClick={() => setActiveFilter(FILTER.PENDING)}
                      className="w-full py-3 px-2 text-center rounded-lg"
                      aria-pressed={activeFilter === FILTER.PENDING}>
                      <p className="text-2xl font-bold text-orange-400 tabular-nums">{stats?.pending_payment ?? 0}</p>
                      <p className="text-xs text-neutral-400">Pending</p>
                      <p className="text-[10px] text-neutral-600 mt-0.5">Unpaid</p>
                    </button>
                  </CardContent>
                </Card>

                <Card className={statCardCls(FILTER.CONFIRMED)}>
                  <CardContent className="p-0">
                    <button type="button" onClick={() => setActiveFilter(FILTER.CONFIRMED)}
                      className="w-full py-3 px-2 text-center rounded-lg"
                      aria-pressed={activeFilter === FILTER.CONFIRMED}>
                      <p className="text-2xl font-bold text-empire-gold tabular-nums">{stats?.confirmed ?? 0}</p>
                      <p className="text-xs text-neutral-400">Confirmed</p>
                      <p className="text-[10px] text-neutral-600 mt-0.5">Paid</p>
                    </button>
                  </CardContent>
                </Card>

                <Card className={statCardCls(FILTER.CHECKED_IN)}>
                  <CardContent className="p-0">
                    <button type="button" onClick={() => setActiveFilter(FILTER.CHECKED_IN)}
                      className="w-full py-3 px-2 text-center rounded-lg"
                      aria-pressed={activeFilter === FILTER.CHECKED_IN}>
                      <p className="text-2xl font-bold text-green-400 tabular-nums">
                        {stats?.checked_in ?? 0}
                        {(stats?.total ?? 0) > 0 && (
                          <span className="text-xs font-semibold text-neutral-500 ml-1">
                            ({Math.round(((stats?.checked_in ?? 0) / (stats?.total ?? 1)) * 100)}%)
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-neutral-400">Checked In</p>
                      <p className="text-[10px] text-neutral-600 mt-0.5">Scanned</p>
                    </button>
                  </CardContent>
                </Card>

                <Card className={statCardCls(FILTER.NO_SHOW)}>
                  <CardContent className="p-0">
                    <button type="button" onClick={() => setActiveFilter(FILTER.NO_SHOW)}
                      className="w-full py-3 px-2 text-center rounded-lg"
                      aria-pressed={activeFilter === FILTER.NO_SHOW}>
                      <p className="text-2xl font-bold text-amber-400 tabular-nums">{stats?.no_show ?? 0}</p>
                      <p className="text-xs text-neutral-400">No Show</p>
                      <p className="text-[10px] text-neutral-600 mt-0.5">Absent</p>
                    </button>
                  </CardContent>
                </Card>

              </div>

              {/* ── Read-only notice for past events ─────────────────── */}
              {stats?.event_ended && (
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-neutral-700 bg-neutral-900/60 text-sm text-neutral-400">
                  <AlertCircle className="w-4 h-4 text-neutral-500 flex-shrink-0" />
                  <span>
                    This event has ended. The roster is <span className="text-neutral-200 font-medium">read-only</span> — check-in and payment actions are disabled.
                  </span>
                </div>
              )}

              {/* ── Row 4: Bookings table ─────────────────────────────── */}
              <Card className="card-empire">
                <CardHeader className="pb-3 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <Users className="w-4 h-4 text-empire-gold" />
                      Bookings
                      <span className="text-neutral-400 font-normal text-sm">
                        ({filteredBookings.length}
                        {activeFilter !== FILTER.ALL || searchQuery
                          ? ` of ${roster?.bookings?.length ?? 0}`
                          : ""})
                      </span>
                    </CardTitle>
                    {(activeFilter !== FILTER.ALL || searchQuery) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setActiveFilter(FILTER.ALL); setSearchQuery(""); }}
                        className="text-empire-gold hover:text-empire-gold/80 text-xs shrink-0"
                      >
                        Clear filters
                      </Button>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                      <Input
                        placeholder="Search by name, email, phone, reference or ticket type..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="input-empire pl-9 h-10 text-sm"
                      />
                    </div>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      aria-label="Sort bookings"
                      className="input-empire h-10 text-sm px-3 sm:w-[190px] bg-neutral-900 border border-neutral-700 rounded-md text-neutral-200"
                    >
                      <option value="recent">Most recent first</option>
                      <option value="oldest">Oldest first</option>
                      <option value="amount">Highest amount</option>
                      <option value="tickets">Most tickets</option>
                      <option value="name">Name (A–Z)</option>
                    </select>
                  </div>

                  {activeFilter !== FILTER.ALL && (
                    <p className="text-xs text-neutral-500">
                      Filter:{" "}
                      <span className="text-neutral-300">
                        {activeFilter === FILTER.PENDING && "Pending payment"}
                        {activeFilter === FILTER.CONFIRMED && "Confirmed (paid, not yet checked in)"}
                        {activeFilter === FILTER.CHECKED_IN && "Checked in"}
                        {activeFilter === FILTER.NO_SHOW && "No show (paid, event ended, absent)"}
                      </span>
                    </p>
                  )}
                </CardHeader>

                <CardContent>
                  {(roster?.bookings?.length ?? 0) === 0 ? (
                    <div className="text-center py-12 text-neutral-500">
                      <Ticket className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      <p>No bookings yet for this event</p>
                    </div>
                  ) : filteredBookings.length === 0 ? (
                    <div className="text-center py-12 text-neutral-500">
                      <Search className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      <p>No bookings match your search or filter</p>
                      <Button
                        variant="link"
                        className="text-empire-gold mt-2 text-sm"
                        onClick={() => { setActiveFilter(FILTER.ALL); setSearchQuery(""); }}
                      >
                        Clear and show all
                      </Button>
                    </div>
                  ) : (
                    <AnimatePresence mode="popLayout">
                      <div className="space-y-3">
                        {paginatedBookings.map((booking, idx) => {
                          const isCheckedIn = Boolean(booking.checked_in_at);
                          const isNoShow = booking.is_no_show;
                          const isPaid = booking.payment_status === "paid";
                          const rowBg = isCheckedIn
                            ? "bg-green-500/5 border-green-500/20"
                            : isNoShow
                            ? "bg-amber-500/5 border-amber-500/20"
                            : "bg-neutral-900/50 border-neutral-800";

                          return (
                            <motion.div
                              key={booking.booking_id}
                              initial={{ opacity: 0, y: 4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              transition={{ delay: Math.min(idx * 0.02, 0.3) }}
                              className={`p-4 rounded-xl border ${rowBg}`}
                            >
                              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                                {/* Guest info */}
                                <div className="flex items-start gap-3">
                                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                                    isCheckedIn ? "bg-green-500/20" : isNoShow ? "bg-amber-500/20" : "bg-neutral-800"
                                  }`}>
                                    {isCheckedIn ? (
                                      <CheckCircle className="w-4 h-4 text-green-400" />
                                    ) : isNoShow ? (
                                      <UserX className="w-4 h-4 text-amber-400" />
                                    ) : (
                                      <User className="w-4 h-4 text-neutral-400" />
                                    )}
                                  </div>

                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="font-medium text-white text-sm">
                                        {booking.guest_name || "Guest"}
                                      </p>
                                      {isPaid ? (
                                        <Badge className="bg-empire-gold/20 text-empire-gold border-empire-gold/30 text-xs">Paid</Badge>
                                      ) : (
                                        <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">Pending</Badge>
                                      )}
                                      {isCheckedIn && (
                                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                                          <CheckCircle className="w-3 h-3 mr-1" />Checked In
                                        </Badge>
                                      )}
                                      {isNoShow && (
                                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
                                          <UserX className="w-3 h-3 mr-1" />No Show
                                        </Badge>
                                      )}
                                    </div>

                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-neutral-400 mt-1">
                                      {booking.guest_email && (
                                        <span className="flex items-center gap-1">
                                          <Mail className="w-3 h-3" />{booking.guest_email}
                                        </span>
                                      )}
                                      {booking.guest_phone && (
                                        <span className="flex items-center gap-1">
                                          <Phone className="w-3 h-3" />{booking.guest_phone}
                                        </span>
                                      )}
                                    </div>

                                    {booking.booking_number && (
                                      <p className="text-xs text-neutral-500 mt-1">
                                        Ref: <span className="font-mono">{booking.booking_number}</span>
                                        {booking.created_at && (
                                          <span className="ml-2 text-neutral-600">· {formatEAT(booking.created_at)}</span>
                                        )}
                                      </p>
                                    )}

                                    {(booking.mpesa_receipt_number || booking.payment_reference) && (
                                      <p className="text-xs text-neutral-500 mt-0.5 flex flex-wrap gap-x-3">
                                        {booking.mpesa_receipt_number && (
                                          <span>
                                            M-Pesa:{" "}
                                            <span className="font-mono text-empire-gold">
                                              {booking.mpesa_receipt_number}
                                            </span>
                                          </span>
                                        )}
                                        {booking.mpesa_phone_number && (
                                          <span className="font-mono text-neutral-600">
                                            {booking.mpesa_phone_number}
                                          </span>
                                        )}
                                        {booking.paid_at && (
                                          <span className="text-neutral-600">Paid {booking.paid_at}</span>
                                        )}
                                      </p>
                                    )}

                                    {!isPaid && booking.failure_reason && (
                                      <p className="text-xs text-orange-400/80 mt-0.5 capitalize">
                                        {booking.failure_reason.replace(/_/g, " ")}
                                      </p>
                                    )}
                                  </div>
                                </div>

                                {/* Ticket + amount + check-in action */}
                                <div className="flex flex-col items-start sm:items-end gap-2 sm:min-w-[160px]">
                                  {booking.ticket_type_name && (
                                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                                      <Ticket className="w-3.5 h-3.5 text-empire-gold flex-shrink-0" />
                                      <span className="text-sm text-white">{booking.ticket_type_name}</span>
                                      {booking.ticket_quantity > 1 && (
                                        <span className="text-xs text-neutral-400">×{booking.ticket_quantity}</span>
                                      )}
                                      {booking.ticket_type_category && (
                                        <Badge className={`text-[10px] px-1.5 py-0 ${TICKET_TYPE_COLORS[booking.ticket_type_category] || TICKET_TYPE_COLORS.regular}`}>
                                          {TICKET_TYPE_LABELS[booking.ticket_type_category] || booking.ticket_type_category}
                                        </Badge>
                                      )}
                                    </div>
                                  )}

                                  <p className="text-sm font-bold text-white">
                                    KES {(booking.total_amount || 0).toLocaleString()}
                                  </p>

                                  {isCheckedIn && booking.checked_in_at && (
                                    <p className="text-xs text-green-400 flex items-center gap-1">
                                      <Clock className="w-3 h-3" />{formatEAT(booking.checked_in_at)}
                                    </p>
                                  )}

                                  {!isCheckedIn && isPaid && !isNoShow && !stats?.event_ended && (
                                    <Button
                                      size="sm"
                                      onClick={() => handleCheckIn(booking.booking_id, booking.guest_name)}
                                      className="h-7 text-xs bg-empire-gold/10 hover:bg-empire-gold/20 text-empire-gold border border-empire-gold/30 hover:border-empire-gold/50"
                                      variant="ghost"
                                    >
                                      <DoorOpen className="w-3 h-3 mr-1" />
                                      Check In
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-800">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="text-neutral-400 hover:text-white disabled:opacity-30"
                          >
                            <ChevronRight className="w-4 h-4 mr-1 rotate-180" />
                            Prev
                          </Button>
                          <span className="text-xs text-neutral-500">
                            Page {page} of {totalPages} · {filteredBookings.length} total
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="text-neutral-400 hover:text-white disabled:opacity-30"
                          >
                            Next
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      )}
                    </AnimatePresence>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <Card className="card-empire">
              <CardContent className="py-20 text-center">
                <AlertCircle className="w-10 h-10 mx-auto mb-3 text-neutral-600" />
                <p className="text-neutral-400">Select an event to view its roster</p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
